import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { Box, CircularProgress, Typography } from '@mui/material';
import QuadPanel from 'features/IRWorkstation/components/QuadPanel';
import AlertsRow from 'features/IRWorkstation/components/AlertsRow';
import inputSignalsService from 'services/inputSignalsService';

const AlertsWidget = ({ onOpenAlertsDrawer }) => {
	const { tenantId } = useParams();
	const [loading, setLoading] = useState(true);
	const [alerts, setAlerts] = useState([]);
	const [error, setError] = useState(null);

	useEffect(() => {
		const fetchAlerts = async () => {
			try {
				setLoading(true);
				setError(null);

				const alerts = await inputSignalsService.getInputSignals({
					event_category: 'alerts',
					event_status: 'unacknowledged',
				});

				setAlerts(alerts);
			} catch (err) {
				console.error('Failed to fetch input signals:', err);
				setError('Failed to load alerts.');
			} finally {
				setLoading(false);
			}
		};
		fetchAlerts();
	}, [tenantId]);

	const AlertList = () =>
		alerts.map((alert) => (
			<AlertsRow
				key={`alert-${alert.id}`}
				alert={alert}
				onClick={alert ? () => onOpenAlertsDrawer(alert) : undefined}
			/>
		));

	const LoadingIndicator = () => (
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				padding: '40px',
				flexDirection: 'column',
				gap: 2,
				height: '100%',
			}}
		>
			<CircularProgress size={18} />
			<Typography variant='body2' color='text.secondary'>
				Loading alerts...
			</Typography>
		</Box>
	);

	const ErrorMessage = () => (
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				padding: '40px',
				height: '100%',
			}}
		>
			<Typography variant='body2' color='error'>
				{error}
			</Typography>
		</Box>
	);

	const EmptyState = () => (
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				padding: '40px',
				height: '100%',
			}}
		>
			<Typography variant='body2' color='text.secondary'>
				No Alerts.
			</Typography>
		</Box>
	);

	return (
		<QuadPanel
			title='Alerts'
			badge={`${alerts.length} unacknowledged`}
			badgeColor='red'
			subtitle={
				<>
					Purple alerts sourced from Raise VDR —{' '}
					<Box
						component='span'
						sx={{
							fontSize: '10px',
							fontWeight: 600,
							color: 'vdr.text',
							backgroundColor: 'vdr.bg',
							border: '0.5px solid',
							borderColor: 'vdr.border',
							borderRadius: '3px',
							padding: '2px 6px',
							cursor: 'pointer',
							'&:hover': { backgroundColor: 'vdr.hover' },
						}}
					>
						↔ All VDR alerts
					</Box>
				</>
			}
		>
			{loading ? (
				<LoadingIndicator />
			) : error ? (
				<ErrorMessage />
			) : alerts.length === 0 ? (
				<EmptyState />
			) : (
				<AlertList />
			)}
		</QuadPanel>
	);
};

AlertsWidget.propTypes = {
	onOpenAlertsDrawer: PropTypes.func.isRequired,
};

export default AlertsWidget;
